import { CLASSIC_SHAPES, SHAPES, randomSource, validateDesign, type LevelDesign } from './generator';
import { CHESS_SHAPES } from './chessShapes';
import { THEMED_SHAPE_GROUPS } from './themedShapes';
import type { ShapeName } from './types';
import { assertCustomLevel } from './openingLevels';

export type ShapeGroup = { id: string; name: string; shapes: ShapeName[] };
export const BUILTIN_SHAPE_GROUPS: ShapeGroup[] = [
  { id: 'all', name: 'All shapes', shapes: [...SHAPES] },
  { id: 'classic', name: 'Classic shapes', shapes: [...CLASSIC_SHAPES] },
  { id: 'chess', name: 'Chess pieces', shapes: [...CHESS_SHAPES] },
  ...THEMED_SHAPE_GROUPS
];
export function validateShapeGroups(groups: ShapeGroup[]): void {
  if (!Array.isArray(groups) || groups.length > 20) throw new Error('You can save up to 20 custom shape groups.');
  const ids = new Set<string>();
  const names = new Set(BUILTIN_SHAPE_GROUPS.map(group => group.name.toLowerCase()));
  for (const group of groups) {
    if (!group || typeof group.id !== 'string' || !/^group-[a-zA-Z0-9-]{1,64}$/.test(group.id) || ids.has(group.id)) throw new Error('Invalid or duplicate shape-group ID.');
    if (typeof group.name !== 'string' || !group.name.trim() || group.name.length > 40 || names.has(group.name.trim().toLowerCase())) throw new Error('Give each group a unique name, up to 40 characters.');
    if (!Array.isArray(group.shapes) || !group.shapes.length || group.shapes.length > SHAPES.length || new Set(group.shapes).size !== group.shapes.length || group.shapes.some(shape => !SHAPES.includes(shape))) throw new Error('Choose at least one supported shape, without duplicates.');
    ids.add(group.id); names.add(group.name.trim().toLowerCase());
  }
}
/** Each shuffled cycle uses every group member exactly once, including at cycle boundaries. */
export function planBatch(base: LevelDesign, count: number, shapes: ShapeName[]): LevelDesign[] {
  assertCustomLevel(base.number);
  if (!Number.isInteger(count) || count < 1 || count > 20 || base.number + count - 1 > 100) throw new Error('Choose 1–20 levels within slots 4–100.');
  if (!Array.isArray(shapes) || !shapes.length || shapes.length > SHAPES.length || new Set(shapes).size !== shapes.length || shapes.some(shape => !SHAPES.includes(shape))) throw new Error('Choose a group with supported, unique shapes.');
  // The template shape is irrelevant to mixed batches; each planned board is validated below.
  validateDesign({ ...base, shape: 'rectangle' });
  const rand = randomSource(base.seed);
  const plan: LevelDesign[] = [];
  let previous: ShapeName | undefined;
  while (plan.length < count) {
    const cycle = [...shapes];
    for (let i = cycle.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [cycle[i], cycle[j]] = [cycle[j]!, cycle[i]!];
    }
    if (cycle.length > 1 && cycle[0] === previous) {
      const index = 1 + Math.floor(rand() * (cycle.length - 1));
      [cycle[0], cycle[index]] = [cycle[index]!, cycle[0]!];
    }
    for (const shape of cycle) {
      const i = plan.length;
      const design = { ...base, number: base.number + i, seed: (base.seed + i) >>> 0, shape, height: shape === 'square' ? base.width : base.height };
      validateDesign(design);
      plan.push(design); previous = shape;
      if (plan.length === count) break;
    }
  }
  return plan;
}
